import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { UserPlus, Loader2 } from 'lucide-react'
import { templatePermissionService } from '@/lib/api/services/templatePermissions'
import type { AccessScopeType, PermissionLevel } from '@/lib/api/types'
import {
  PERMISSION_LEVELS,
  ACCESS_SCOPE_TYPES,
} from '@/lib/api/types'
import { useToast } from '@/components/ui/use-toast'

interface AddPermissionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  templateId: string
  onSuccess: () => void
}

export function AddPermissionDialog({
  open,
  onOpenChange,
  templateId,
  onSuccess,
}: AddPermissionDialogProps) {
  const [loading, setLoading] = useState(false)
  const [scopeType, setScopeType] = useState<AccessScopeType>('User')
  const [scopeId, setScopeId] = useState('')
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>('View')
  const [expiresAt, setExpiresAt] = useState('')
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (scopeType !== 'Everyone' && !scopeId) {
      toast({
        title: 'Error',
        description: 'Please select a user, role, or team',
        variant: 'destructive',
      })
      return
    }

    setLoading(true)
    try {
      await templatePermissionService.grantPermission(templateId, {
        scopeType,
        scopeId: scopeType !== 'Everyone' ? scopeId : undefined,
        permissionLevel,
        expiresAt: expiresAt || undefined,
      })

      toast({
        title: 'Permission granted',
        description: 'Access has been successfully granted',
      })

      onSuccess()
      onOpenChange(false)
      resetForm()
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to grant permission',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setScopeType('User')
    setScopeId('')
    setPermissionLevel('View')
    setExpiresAt('')
  }

  // Filter out non-selectable scope types
  const selectableScopeTypes = ACCESS_SCOPE_TYPES.filter(
    (s) => s.value !== 'InheritFromHub'
  )

  // Filter out None permission level for granting
  const grantablePermissionLevels = PERMISSION_LEVELS.filter(
    (p) => p.value !== 'None'
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600">
              <UserPlus className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle>Add People</DialogTitle>
              <DialogDescription>
                Grant access to this template
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Scope Type */}
          <div className="space-y-2">
            <Label>Access Type</Label>
            <Select
              value={scopeType}
              onValueChange={(v) => setScopeType(v as AccessScopeType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {selectableScopeTypes.map((scope) => (
                  <SelectItem key={scope.value} value={scope.value}>
                    <div className="flex flex-col">
                      <span>{scope.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {scope.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Scope ID - Show for User, Role, Team, Department */}
          {scopeType !== 'Everyone' && (
            <div className="space-y-2">
              <Label>
                {scopeType === 'User' && 'User ID or Email'}
                {scopeType === 'Role' && 'Role ID'}
                {scopeType === 'Team' && 'Team ID'}
                {scopeType === 'Department' && 'Department ID'}
              </Label>
              <Input
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
                placeholder={`Enter ${scopeType.toLowerCase()} ID`}
              />
              <p className="text-xs text-muted-foreground">
                Enter the ID of the {scopeType.toLowerCase()} to grant access to
              </p>
            </div>
          )}

          {/* Permission Level */}
          <div className="space-y-2">
            <Label>Permission Level</Label>
            <Select
              value={permissionLevel}
              onValueChange={(v) => setPermissionLevel(v as PermissionLevel)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {grantablePermissionLevels.map((level) => (
                  <SelectItem key={level.value} value={level.value}>
                    <div className="flex flex-col">
                      <span>{level.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {level.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Expiration Date (Optional) */}
          <div className="space-y-2">
            <Label>Expiration Date (Optional)</Label>
            <Input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty for permanent access
            </p>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Grant Access
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
